import { useEffect, useRef, useState } from 'react';
import { resolveAgencyLogoUrl, agencyInitials, invalidateAgencyLogoCache } from '@/lib/agency-logo';
import { cn } from '@/lib/utils';
import { Building2 } from 'lucide-react';

type Props = {
  name?: string | null;
  logoUrl?: string | null;
  size?: number;
  className?: string;
  rounded?: 'sm' | 'md' | 'full';
  /** For a11y — pass the agency name when the logo is decorative next to a label. */
  alt?: string;
  /**
   * Fallback style when the logo is missing or fails to load.
   *  - 'initials' (default) → pastel avatar with agency initials
   *  - 'flowtokos' → neutral FlowTokOS building icon (used inside contract/proposal headers)
   */
  fallback?: 'initials' | 'flowtokos';
  /** Object-fit override. Defaults to 'cover' but 'contain' is often better in documents. */
  fit?: 'cover' | 'contain';
};

const bgSeed = (name?: string | null) => {
  let h = 0;
  for (const c of (name || 'A')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const hue = h % 360;
  return `hsl(${hue}, 65%, 45%)`;
};

/**
 * Robust agency-logo renderer used everywhere in the platform.
 * - Resolves storage URLs to short-lived signed URLs on the fly.
 * - Self-heals stale signed URLs by force-refreshing once before giving up.
 * - Falls back to an initials avatar (or FlowTokOS icon) on any load failure — never leaks a broken image icon.
 */
export function AgencyLogo({
  name, logoUrl, size = 24, className, rounded = 'md', alt,
  fallback = 'initials', fit = 'cover',
}: Props) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const retriedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setSrc(null);
    retriedRef.current = false;
    if (!logoUrl) return;
    resolveAgencyLogoUrl(logoUrl).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => { cancelled = true; };
  }, [logoUrl]);

  const dims = { width: size, height: size };
  const radius = rounded === 'full' ? '9999px' : rounded === 'sm' ? '4px' : '6px';

  const handleError = () => {
    if (!retriedRef.current) {
      // Self-heal: the signed URL may have expired mid-flight. Bust cache and mint a new one once.
      retriedRef.current = true;
      invalidateAgencyLogoCache(logoUrl);
      resolveAgencyLogoUrl(logoUrl, { forceRefresh: true }).then((url) => {
        if (url && url !== src) setSrc(url);
        else setFailed(true);
      }).catch(() => setFailed(true));
      return;
    }
    // eslint-disable-next-line no-console
    console.warn('[agency-logo] failed to load', { name, logoUrl, resolved: src });
    setFailed(true);
  };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={alt ?? (name ? `${name} logo` : '')}
        style={{ ...dims, borderRadius: radius }}
        className={cn(fit === 'contain' ? 'object-contain' : 'object-cover', 'flex-shrink-0 bg-muted', className)}
        onError={handleError}
      />
    );
  }

  if (fallback === 'flowtokos') {
    return (
      <div
        style={{ ...dims, borderRadius: radius, fontSize: Math.max(9, Math.round(size * 0.42)) }}
        className={cn(
          'flex items-center justify-center bg-muted text-muted-foreground flex-shrink-0 select-none border border-border',
          className,
        )}
        aria-label={alt ?? (name ? `${name} logo` : 'Agency')}
        role="img"
      >
        <Building2 style={{ width: size * 0.55, height: size * 0.55 }} />
      </div>
    );
  }

  return (
    <div
      style={{
        ...dims,
        borderRadius: radius,
        background: bgSeed(name),
        fontSize: Math.max(9, Math.round(size * 0.42)),
      }}
      className={cn(
        'flex items-center justify-center text-white font-semibold flex-shrink-0 select-none',
        className,
      )}
      aria-label={alt ?? (name ? `${name} logo` : 'Agency')}
      role="img"
    >
      {agencyInitials(name)}
    </div>
  );
}
